import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from '@components/common/buttons/Button';
import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import DurationInput from '@components/common/inputs/duration/DurationInput';
import EmojiPicker from '@components/common/emoji/EmojiPicker.loader';
import { EmojiPreview } from '@components/common/emoji/EmojiPreview';
import { RegularText } from '@utils/typography';

import { PAGE_PRESETS, RATE_PRESET_LABELS, RATE_PRESETS_SECONDS, TIME_PRESET_LABELS, TIME_PRESETS } from './constants';
import {
  CatTitleInput,
  CatTitleName,
  Chip,
  ChipDot,
  ChipX,
  ConvertChoice,
  ConvertChoices,
  CustomPop,
  DefaultLabel,
  DefaultRow,
  EditableName,
  EmojiBtn,
  FieldError,
  NameInput,
  PagesInput,
  Pill,
  PillCaption,
  PillGroup,
  PillWithCaption
} from './TaskEstimator.styles';
import { ChipTone, EstimatorCategory, EstimatorReadingSource, EstimatorTask, NameEditType } from './types';
import { EstimatorDispatch } from './useTaskEstimator';
import { formatPillMinutes, formatRateLabel, isPagePreset, isRatePreset, timePillValues } from './utils';

type Dispatch = EstimatorDispatch;

const timeLabel = (minutes: number) => TIME_PRESET_LABELS[TIME_PRESETS.indexOf(minutes)] ?? formatPillMinutes(minutes);

/**
 * `mark` puts a caption under one pill ("recommended", "last estimate"), with a hover explaining it, adding a pill for
 * its time when that isn't a preset.
 */
export const TimePills = ({
  currentMinutes,
  selected,
  onPreset,
  onCustomToggle,
  positive,
  trailing,
  mark
}: {
  currentMinutes: number | null;
  selected: boolean;
  onPreset: (minutes: number) => void;
  onCustomToggle: () => void;
  positive?: boolean;
  trailing?: ReactNode;
  mark?: { minutes: number; label: string; hover?: string } | null;
}) => {
  const values = timePillValues(mark?.minutes);
  const isCustom = currentMinutes != null && !values.includes(currentMinutes);
  return (
    <PillGroup style={mark ? { paddingBottom: 18 } : undefined}>
      {values.map(val => {
        const isSelected = selected && !isCustom && currentMinutes === val;
        const pill = (
          <Pill
            key={val}
            $selected={isSelected}
            data-selected={isSelected}
            $positive={positive}
            onClick={() => onPreset(val)}
          >
            {timeLabel(val)}
          </Pill>
        );
        if (val !== mark?.minutes) return pill;
        // The explanation is on the caption itself, dotted underlined as in the Auto-plan dialog's hints.
        return (
          <PillWithCaption key={val}>
            {pill}
            <PillCaption>
              {mark.hover ? (
                <TippyTooltip
                  position="top"
                  width="260px"
                  target={<DottedCaption>{mark.label}</DottedCaption>}
                  content={mark.hover}
                />
              ) : (
                mark.label
              )}
            </PillCaption>
          </PillWithCaption>
        );
      })}
      <Pill $selected={isCustom} data-selected={isCustom} $positive={positive} onClick={onCustomToggle}>
        {isCustom && currentMinutes != null ? formatPillMinutes(currentMinutes) : 'Custom'}
      </Pill>
      {trailing}
    </PillGroup>
  );
};

export const PagePills = ({
  currentPages,
  onPreset,
  onCustomToggle,
  trailing,
  // Narrower callers can offer fewer picks; the labels are just the counts, so they follow whatever is passed.
  presets = PAGE_PRESETS
}: {
  currentPages: number | null;
  onPreset: (pages: number) => void;
  onCustomToggle: () => void;
  trailing?: ReactNode;
  presets?: number[];
}) => {
  const isCustom = currentPages != null && !isPagePreset(currentPages);
  return (
    <PillGroup>
      {presets.map(val => (
        <Pill
          key={val}
          $selected={!isCustom && currentPages === val}
          data-selected={!isCustom && currentPages === val}
          onClick={() => onPreset(val)}
        >
          {`${val}p`}
        </Pill>
      ))}
      <Pill $selected={isCustom} data-selected={isCustom} onClick={onCustomToggle}>
        {isCustom && currentPages != null ? `${currentPages}p` : 'Custom'}
      </Pill>
      {trailing}
    </PillGroup>
  );
};

export const RatePills = ({
  secondsPerPage,
  onPreset,
  onCustomToggle,
  trailing
}: {
  secondsPerPage: number;
  onPreset: (seconds: number) => void;
  onCustomToggle: () => void;
  trailing?: ReactNode;
}) => {
  const isCustom = !isRatePreset(secondsPerPage);
  return (
    <PillGroup>
      {RATE_PRESETS_SECONDS.map((val, i) => (
        <Pill
          key={val}
          $selected={!isCustom && secondsPerPage === val}
          data-selected={!isCustom && secondsPerPage === val}
          $positive
          onClick={() => onPreset(val)}
        >
          {RATE_PRESET_LABELS[i]}
        </Pill>
      ))}
      <Pill $selected={isCustom} data-selected={isCustom} $positive onClick={onCustomToggle}>
        {isCustom ? formatRateLabel(secondsPerPage) : 'Custom'}
      </Pill>
      {trailing}
    </PillGroup>
  );
};

/**
 * `autoFocus` is off where the field opens by itself because the saved value isn't a preset, rather than because the
 * student clicked Custom: on the Estimator's first paint it would scroll the list down to that field.
 */
export const CustomDurationPop = ({
  value,
  inSeconds,
  onApply,
  autoFocus = true
}: {
  value: number | null;
  inSeconds?: boolean;
  onApply: (value: number) => void;
  autoFocus?: boolean;
}) => {
  const [draft, setDraft] = useState<number | undefined>(value ?? undefined);
  const empty = draft == null || draft <= 0;

  useEffect(() => {
    setDraft(value ?? undefined);
  }, [value]);

  // Like the pages field: Apply stays greyed out until there is a time above zero, and Enter waits for one too.
  // The field only ever holds a valid duration, so an empty one is the only thing to stop.
  const apply = () => {
    if (empty) return;
    onApply(draft!);
  };

  return (
    <CustomPop>
      <DurationInput
        inline
        noErrorMargin
        autofocus={autoFocus}
        selectOnFocus
        required={false}
        inSeconds={inSeconds}
        value={draft}
        onChange={setDraft}
        onKeyUp={e => {
          if (e.key === 'Enter') apply();
        }}
      />
      <Button size="sm" filled disabled={empty} onClick={apply}>
        Apply
      </Button>
    </CustomPop>
  );
};

/**
 * `bare` when the field is the only control rather than the one under the presets: it loses the dashed line that
 * separates it from them. `autoFocus` is off where several of these sit in one list, since each would take the focus
 * from the last and scroll the list to it.
 */
export const CustomPagesPop = ({
  value,
  onApply,
  bare = false,
  autoFocus = true
}: {
  value: string;
  onApply: (raw: string) => boolean;
  bare?: boolean;
  autoFocus?: boolean;
}) => {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const empty = draft.trim() === '';

  useEffect(() => {
    setDraft(value);
    if (!autoFocus) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [value, autoFocus]);

  // Nothing to read yet: Apply stays greyed out, and Enter does nothing rather than complain.
  const apply = () => {
    if (empty) return;
    const ok = onApply(draft);
    setError(ok ? '' : 'Enter a page count (25) or range (20-44).');
  };

  const Container = bare ? BareCustomPop : CustomPop;
  return (
    <Container>
      <PagesInput
        ref={inputRef}
        placeholder="25 or 20-44"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          }
        }}
      />
      <Button size="sm" filled disabled={empty} onClick={apply}>
        Apply
      </Button>
      <FieldError>{error}</FieldError>
    </Container>
  );
};

const BareCustomPop = styled(CustomPop)`
  padding-top: 0;
  margin-top: 0;
  border-top: none;
`;

export const NameField = ({
  type,
  id,
  name,
  editing,
  catTitle,
  trashed,
  dispatch
}: {
  type: NameEditType;
  id: number;
  name: string;
  editing: boolean;
  catTitle?: boolean;
  trashed?: boolean;
  dispatch: Dispatch;
}) => {
  const widthCh = Math.min(Math.max(name.length + 2, 8), 40);
  const Input = catTitle ? CatTitleInput : NameInput;
  const Label = catTitle ? CatTitleName : EditableName;

  if (editing) {
    return (
      <Input
        autoFocus
        defaultValue={name}
        style={{ width: `${widthCh}ch` }}
        onFocus={e => e.currentTarget.select()}
        onBlur={e => dispatch({ type: 'COMMIT_EDIT_NAME', value: e.target.value })}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            dispatch({ type: 'CANCEL_EDIT' });
          }
        }}
      />
    );
  }

  return (
    <Label
      $trashed={trashed}
      title="Click to rename"
      onClick={() => dispatch({ type: 'START_EDIT_NAME', nameType: { type, id } })}
    >
      {name}
    </Label>
  );
};

export const CategoryEmoji = ({ category, dispatch }: { category: EstimatorCategory; dispatch: Dispatch }) => (
  <EmojiPicker
    tooltip="Change emoji"
    target={
      <EmojiBtn>
        <EmojiPreview emoji={category.emoji} size={18} />
      </EmojiBtn>
    }
    onSelect={(emoji: string) => dispatch({ type: 'COMMIT_EMOJI', categoryId: category.id, emoji })}
    onClickOutside={() => {}}
  />
);

export const EstimatorChip = ({
  tone,
  label,
  onClick,
  onClear
}: {
  tone: ChipTone;
  label: string;
  onClick: () => void;
  onClear?: () => void;
}) => (
  <Chip $tone={tone} type="button" onClick={onClick}>
    {tone !== 'quiet' && tone !== 'unset' && <ChipDot />}
    {label}
    {onClear && (
      <ChipX
        onClick={e => {
          e.stopPropagation();
          onClear();
        }}
      >
        ×
      </ChipX>
    )}
  </Chip>
);

// `clear` is what the × on the time does: clear it, go back to the syllabus's pages, or bring a trashed task back.
export const regularChip = (task: EstimatorTask): { tone: ChipTone; label: string; clear?: 'reset' | 'trash' } => {
  if (task.trashed) return { tone: 'excluded', label: 'Trashed', clear: 'trash' };
  // In a category the question above does the estimating, so the row's own picker stays quiet rather than
  // repeating a call to action on every task. Uncategorized tasks have no category time, so theirs stays loud.
  if (task.minutes == null && task.categoryId != null) return { tone: 'quiet', label: 'Estimate' };
  if (task.minutes == null) return { tone: 'unset', label: 'Estimate' };
  return {
    tone: task.categoryId != null ? 'inherited' : 'explicit',
    label: formatPillMinutes(task.minutes) || '',
    clear: 'reset'
  };
};

export const readingChip = (
  task: EstimatorTask,
  wasOverridden: boolean
): { tone: ChipTone; label: string; clear?: 'detected' | 'reset' | 'trash' } => {
  if (task.trashed) return { tone: 'excluded', label: 'Trashed', clear: 'trash' };
  if (task.pages != null) {
    return {
      tone: 'explicit',
      label: `${task.pages}p · ${formatPillMinutes(Math.round(task.minutes || 0))}`,
      clear: wasOverridden ? 'detected' : 'reset'
    };
  }
  return { tone: 'unset', label: 'Set pages' };
};

export const ConvertPickerPanel = ({
  isReading,
  categories,
  sources,
  onRegular,
  onReading,
  plural
}: {
  isReading: boolean;
  categories: EstimatorCategory[];
  sources: EstimatorReadingSource[];
  onRegular: (categoryId: number | null) => void;
  onReading: (sourceId: number) => void;
  plural?: boolean;
}) => (
  <DefaultRow>
    <DefaultLabel>
      {isReading
        ? `Which category ${plural ? 'do these' : 'does this'} belong to?`
        : `Which reading source ${plural ? 'do these' : 'does this'} belong to?`}
    </DefaultLabel>
    <ConvertChoices>
      {isReading ? (
        <>
          {categories.map(category => (
            <ConvertChoice key={category.id} type="button" onClick={() => onRegular(category.id)}>
              {category.emoji} {category.name}
            </ConvertChoice>
          ))}
          <ConvertChoice type="button" onClick={() => onRegular(null)}>
            No category
          </ConvertChoice>
        </>
      ) : sources.length > 0 ? (
        sources.map(source => (
          <ConvertChoice key={source.id} type="button" onClick={() => onReading(source.id)}>
            {source.name}
          </ConvertChoice>
        ))
      ) : (
        <RegularText>No reading sources in this course.</RegularText>
      )}
    </ConvertChoices>
  </DefaultRow>
);

export const CategoryPickerPanel = ({
  categories,
  excludeCategoryId,
  onSelect,
  plural
}: {
  categories: EstimatorCategory[];
  excludeCategoryId?: number | null;
  onSelect: (categoryId: number | null) => void;
  plural?: boolean;
}) => (
  <DefaultRow>
    <DefaultLabel>Move {plural ? 'these tasks' : 'this task'} to which category?</DefaultLabel>
    <ConvertChoices>
      {categories
        .filter(category => category.id !== excludeCategoryId)
        .map(category => (
          <ConvertChoice key={category.id} type="button" onClick={() => onSelect(category.id)}>
            {category.emoji} {category.name}
          </ConvertChoice>
        ))}
      {excludeCategoryId !== null && (
        <ConvertChoice type="button" onClick={() => onSelect(null)}>
          No category
        </ConvertChoice>
      )}
    </ConvertChoices>
  </DefaultRow>
);

// The dotted underline the Auto-plan dialog uses for words with an explanation behind a hover.
const DottedCaption = styled.span`
  border-bottom: 1px dotted currentColor;
  cursor: default;
`;
